// Shared existing patient attachment format. Consent evidence uses strict decryption.
async function deriveIV(filename, key) {
  const exportedKey = await crypto.subtle.exportKey("raw", key);
  const keyArray = new Uint8Array(exportedKey);
  // Convert to hex string for easy comparison
  const keyHex = Array.from(keyArray)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

  const enc = new TextEncoder();
  const data = enc.encode(filename + keyHex);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  // console.log('the Javascript key hex is ', keyHex);
  return new Uint8Array(hashBuffer).slice(0, 12);
}

export async function deriveKey(patientId) {
  const enc = new TextEncoder();
  const passwordBytes = enc.encode(patientId + process.env.REACT_APP_ENCRYPTION_SECRET);
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    passwordBytes,
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  const key = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: enc.encode(patientId + "tikcare_salt"),
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  return key;
}

// The IV is derived from the name the object is stored under, because every
// reader derives it again from the storage key's last segment. Pass `ivName`
// whenever the storage name differs from the browser file name.
export async function encryptFile(file, key, ivName = null) {
  const iv = await deriveIV(ivName || file.name, key);
  const buffer = await file.arrayBuffer();
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    buffer
  );
  return new Blob([encrypted]);
}

export async function decryptFile(encryptedBlob, key, filename, { allowLegacy = true } = {}) {
  // File is from after encryption was implemented, so decrypt it
  try {
    const iv = await deriveIV(filename, key);
    const buffer = await encryptedBlob.arrayBuffer();
    const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, buffer);

    // const ivHex = Array.from(iv)
    //   .map(b => b.toString(16).padStart(2, '0'))
    //   .join('');
    // console.log('IV (hex):', ivHex);
    return new Blob([decrypted]);
  } catch (error) {
    if (!allowLegacy) throw new Error("Unable to decrypt consent document.");
    console.error("Decryption failed, file might not be encrypted:", error);
    // If decryption fails, it might be an unencrypted file, return original
    return encryptedBlob;
  }
}
